<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {faChevronLeft, faChevronDown} from '@fortawesome/pro-light-svg-icons';
import {store} from '../services';
import {inject} from 'vue';
import {Theme} from '@teemill/common/classes';
import {image} from '@teemill/common/helpers';

const props = defineProps<{
  direction: 'column' | 'row';
  open?: boolean;
}>();

type MenuSegment = {
  title: string;
  link: string | null;
  mode: 'list' | 'image';
  image: {
    src: string | null;
    title: string | null;
    description: string | null;
    link: string | null;
    overlay: boolean;
    borderRadius: 'sharp' | 'smooth' | 'rounded' | 'circle';
    ratio: number;
    text: {
      alignment: 'left' | 'center' | 'right';
      color: string;
      shadow: 'none' | 'light' | 'medium' | 'heavy';
    };
    button: {
      text: string;
      textColor: string;
      backgroundColor: string;
      borderColor: string;
      borderRadius: 'sharp' | 'smooth' | 'rounded' | 'circle';
      width: 'full' | 'auto';
    };
  };
  items: {
    title: string;
    url: string;
    color: string | null;
    backgroundColor: string | null;
  }[];
};

type MenuItem = {
  title: string;
  segments?: MenuSegment[];
  url?: string;
  color: string | null;
  backgroundColor: string | null;
  submenu: {
    title: string;
    items: {
      text: string;
      link: string;
      color: string | null;
      backgroundColor: string | null;
    }[];
  }[];
};

const theme = inject<() => Theme | undefined>('theme', () => undefined);

const menuItems = computed(() => {
  const menuItems = store.state.subdomain?.mainMenu;

  if (!menuItems || !menuItems.length) {
    return [];
  }

  return menuItems.map(
    (item: {
      text: string;
      href: string;
      color: string;
      backgroundColor: string | null;
      submenu: {
        title: string;
        link: string | null;
        mode: 'list' | 'image';
        image: {
          src: string | null;
          title: string | null;
          description: string | null;
          link: string | null;
        };
        items: {
          text: string;
          link: string;
          color: string;
          backgroundColor: string | null;
        }[];
      }[];
    }) => {
      return {
        title: item.text,
        url: item.href,
        color: item.color,
        backgroundColor: item.backgroundColor,
        segments: item.submenu?.map(segment => {
          return {
            title: segment.title,
            link: segment.link,
            mode: segment.mode,
            image: segment.image,
            items: segment.items.map(item => {
              return {
                title: item.text,
                url: item.link,
                color: item.color,
                backgroundColor: item.backgroundColor,
              };
            }),
          };
        }),
      };
    }
  );
});

const activeMenuItem = ref<MenuItem>();
const activeMenuSegment = ref<MenuSegment>();

const exitTimeout = ref<ReturnType<typeof setTimeout>>();

function onMenuItemMouseLeave() {
  if (!activeMenuItem.value?.segments?.length) {
    activeMenuItem.value = undefined;
    return;
  }

  clearTimeout(exitTimeout.value);
  exitTimeout.value = setTimeout(() => {
    activeMenuItem.value = undefined;
  }, 200);
}

function onMenuItemMouseEnter(item: MenuItem) {
  if (item.segments?.length) {
    activeMenuItem.value = item;
    clearTimeout(exitTimeout.value);
  } else {
    clearTimeout(exitTimeout.value);
    exitTimeout.value = setTimeout(() => {
      activeMenuItem.value = undefined;
    }, 200);
  }
}

function onNestedMenuMouseEnter() {
  clearTimeout(exitTimeout.value);
}

function onMobileMenuItemClick(item: MenuItem) {
  if (item.segments?.length) {
    activeMenuItem.value = item;
  }
}

function onMobileMenuSegmentClick(segment: MenuSegment) {
  activeMenuSegment.value = segment;
}

watch(
  () => props.open,
  value => {
    if (!value) {
      activeMenuItem.value = undefined;
      activeMenuSegment.value = undefined;
    }
  }
);
</script>

<template>
  <div
    class="flex flex-wrap gap-x-1 gap-y-3"
    :style="{
      flexDirection: direction,
    }"
  >
    <template v-if="direction === 'row'">
      <tml-anchor
        v-for="(item, index) in menuItems"
        :key="index"
        class="shrink-0 !text-[--tml-menu-text-color]"
        :text="item.title"
        :style="{
          fontFamily: 'var(--tml-menu-text-font) !important',
          textTransform: 'var(--tml-menu-text-transform) !important',
          backgroundColor: item.backgroundColor || undefined,
          padding: '0.1rem 0.3rem',
          cursor: item.url ? undefined : 'default',
        }"
        :href="item.url"
        @mouseover="onMenuItemMouseEnter(item)"
        @mouseleave="onMenuItemMouseLeave"
      >
        <p
          class="!text-[--tml-menu-text-color]"
          :style="{color: item.color ? `${item.color} !important` : undefined}"
        >
          {{ item.title }}
          <font-awesome-icon
            v-if="item.segments?.length"
            class="ml-[0.09rem] align-[-0.05em] text-sm"
            :icon="faChevronDown"
          />
        </p>
      </tml-anchor>
    </template>
    <transition-group v-else name="slide-right">
      <template v-if="!activeMenuItem">
        <tml-anchor
          v-for="(item, index) in menuItems"
          :key="index"
          class="shrink-0 block !text-[--tml-menu-text-color]"
          :href="item.segments?.length ? '#' : item.url"
          :text="item.title"
          :style="{
            fontFamily: 'var(--tml-menu-text-font) !important',
          }"
          @click="onMobileMenuItemClick(item)"
        >
          <h4
            class="mb-6 !text-[--tml-menu-text-color]"
            :style="{
              color: item.color ? `${item.color} !important` : undefined,
              backgroundColor: item.backgroundColor || undefined,
              padding: '0.1rem 0.3rem',
            }"
          >
            {{ item.title }}
            <font-awesome-icon
              v-if="item.segments?.length"
              class="mr-2 rotate-180 float-right pb-1.5"
              :icon="faChevronLeft"
            />
          </h4>
        </tml-anchor>
      </template>
      <template v-else-if="activeMenuItem && !activeMenuSegment">
        <tml-anchor
          class="text-sm font-bold mb-4 block"
          @click="activeMenuItem = undefined"
        >
          <p class="font-bold mb-2 !text-[--tml-form-text-secondary-color]">
            <font-awesome-icon class="mr-2" :icon="faChevronLeft" /> All
          </p>
        </tml-anchor>
        <tml-anchor
          :href="activeMenuItem.url"
          :class="{'!cursor-default': !activeMenuItem.url}"
        >
          <h4 class="mb-4 !text-[--tml-menu-text-color]">
            {{ activeMenuItem.title }}
          </h4>
        </tml-anchor>
        <div
          v-for="(segment, segmentIndex) in activeMenuItem.segments"
          :key="segmentIndex"
        >
          <tml-anchor
            text="Link"
            href="#"
            @click="onMobileMenuSegmentClick(segment)"
          >
            <h5 class="font-bold mb-4 !text-[--tml-menu-text-color]">
              {{ segment.title }}
              <font-awesome-icon
                class="mr-2 rotate-180 float-right pb-1.5"
                :icon="faChevronLeft"
              />
            </h5>
          </tml-anchor>
        </div>
      </template>
      <template v-else-if="activeMenuSegment">
        <tml-anchor
          class="text-sm font-bold mb-4 block"
          @click="activeMenuSegment = undefined"
        >
          <p class="font-bold mb-2 !text-[--tml-form-text-secondary-color]">
            <font-awesome-icon class="mr-2" :icon="faChevronLeft" />
            {{ activeMenuItem.title }}
          </p>
        </tml-anchor>
        <tml-anchor
          :href="activeMenuSegment.link"
          :class="{'!cursor-default': !activeMenuSegment.link}"
        >
          <h4 class="mb-4 !text-[--tml-menu-text-color]">
            {{ activeMenuSegment.title }}
          </h4>
        </tml-anchor>
        <tml-new-pod
          v-if="
            activeMenuSegment.mode === 'image' && activeMenuSegment.image?.src
          "
          class="mb-2"
          :class="{
            '!cursor-default': !activeMenuSegment.image.link,
          }"
          :image="image(activeMenuSegment.image.src, 600)"
          :title="activeMenuSegment.image.title"
          :copy="activeMenuSegment.image.description"
          :href="activeMenuSegment.image.link"
          :overlay="activeMenuSegment.image.overlay"
          :border-radius="
            {
              sharp: '0px',
              smooth: '4px',
              rounded: '12px',
              circle: '999px',
            }[activeMenuSegment.image.borderRadius] ||
            theme()?.get('image.borderRadius')
          "
          :ratio="
            {
              wide: 0.36,
              short: 0.49,
              '4:3': 0.75,
              square: 1.026,
              tall: 1.5,
            }[activeMenuSegment.image.ratio] || 1.026
          "
          :horizontal-alignment="activeMenuSegment.image.text.alignment"
          :text-color="activeMenuSegment.image.text.color"
          :text-shadow="
            {
              none: '',
              light: '0 2px 4px #0004',
              medium: '0 3px 6px #0006',
              heavy: '0 4px 8px #0008',
            }[activeMenuSegment.image.text.shadow] || ''
          "
          :button-text="activeMenuSegment.image.button.text"
          :button-styles="{
            textColor: activeMenuSegment.image.button.textColor,
            backgroundColor: activeMenuSegment.image.button.backgroundColor,
            borderColor: activeMenuSegment.image.button.borderColor,
            borderRadius:
              {
                sharp: '0px',
                smooth: '4px',
                rounded: '12px',
                circle: '999px',
              }[activeMenuSegment.image.button.borderRadius] || '4px',
            fill: activeMenuSegment.image.button.width === 'full',
          }"
          :lazy-load="false"
          v-on="
            activeMenuSegment?.image.link
              ? {click: () => (activeMenuItem = undefined)}
              : {}
          "
        />
        <template v-else>
          <tml-anchor
            v-for="(item, itemIndex) in activeMenuSegment.items"
            :key="itemIndex"
            class="shrink-0 !text-[--tml-menu-text-color]"
            :text="item.title"
            :href="item.url"
            :style="{
              fontFamily: 'var(--tml-menu-text-font) !important',
            }"
          >
            <p
              class="mb-4 !text-[--tml-menu-text-color]"
              :style="{
                color: item.color ? `${item.color} !important` : undefined,
                backgroundColor: item.backgroundColor || undefined,
                padding: item.backgroundColor ? '0.1rem 0.3rem' : undefined,
              }"
            >
              {{ item.title }}
            </p>
          </tml-anchor>
        </template>
      </template>
    </transition-group>
  </div>
  <template v-if="direction === 'row'">
    <transition name="open-down">
      <div
        v-if="activeMenuItem?.segments?.length"
        class="nested-menu w-full absolute left-0 right-0 top-[100%] bg-[--tml-menu-background-color] z-20 p-8 border-t overflow-y-auto"
        :style="{
          maxHeight:
            theme()?.get('menu.searchBar.show') === '1'
              ? 'calc(100vh - 148px)'
              : 'calc(100vh - 100px)',
        }"
        @mouseover="onNestedMenuMouseEnter"
        @mouseleave="onMenuItemMouseLeave"
      >
        <div class="container">
          <tml-grid :breakpoints="{xs: activeMenuItem.segments.length}">
            <div
              v-for="(segment, segmentIndex) in activeMenuItem.segments"
              :key="segmentIndex"
            >
              <h4 class="font-bold mb-2 !text-[--tml-menu-text-color]">
                <tml-anchor
                  v-if="segment.link"
                  :href="segment.link"
                  class="!text-[--tml-menu-text-color]"
                  @click="activeMenuItem = undefined"
                >
                  {{ segment.title }}
                </tml-anchor>
                <template v-else>
                  {{ segment.title }}
                </template>
              </h4>
              <tml-new-pod
                v-if="segment.mode === 'image' && segment.image?.src"
                class="mb-2 w-[75%]"
                :class="{
                  '!cursor-default': !segment.image.link,
                }"
                :image="image(segment.image.src, 600)"
                :title="segment.image.title"
                :copy="segment.image.description"
                :href="segment.image.link"
                :overlay="segment.image.overlay"
                :border-radius="
                  {
                    sharp: '0px',
                    smooth: '4px',
                    rounded: '12px',
                    circle: '999px',
                  }[segment.image.borderRadius] || '4px'
                "
                :ratio="
                  {
                    wide: 0.36,
                    short: 0.49,
                    '4:3': 0.75,
                    square: 1.026,
                    tall: 1.5,
                  }[segment.image.ratio] || 1.026
                "
                :horizontal-alignment="segment.image.text.alignment"
                :text-color="segment.image.text.color"
                :text-shadow="
                  {
                    none: '',
                    light: '0 2px 4px #0004',
                    medium: '0 3px 6px #0006',
                    heavy: '0 4px 8px #0008',
                  }[segment.image.text.shadow] || ''
                "
                :button-text="segment.image.button.text"
                :button-styles="{
                  textColor: segment.image.button.textColor,
                  backgroundColor: segment.image.button.backgroundColor,
                  borderColor: segment.image.button.borderColor,
                  borderRadius:
                    {
                      sharp: '0px',
                      smooth: '4px',
                      rounded: '12px',
                      circle: '999px',
                    }[segment.image.button.borderRadius] ||
                    theme()?.get('button.borderRadius'),
                  fill: segment.image.button.width === 'full',
                }"
                :lazy-load="false"
                v-on="
                  segment.image.link
                    ? {click: () => (activeMenuItem = undefined)}
                    : {}
                "
              />
              <template v-else>
                <tml-anchor
                  v-for="(item, itemIndex) in segment.items"
                  :key="itemIndex"
                  :href="item.url"
                  class="flex"
                  @click="activeMenuItem = undefined"
                >
                  <p
                    class="mb-2 text-sm !text-[--tml-menu-text-color]"
                    :style="{
                      color: item.color
                        ? `${item.color} !important`
                        : undefined,
                      backgroundColor: item.backgroundColor || undefined,
                      padding: item.backgroundColor
                        ? '0.1rem 0.3rem'
                        : undefined,
                    }"
                  >
                    {{ item.title }}
                  </p>
                </tml-anchor>
              </template>
            </div>
          </tml-grid>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="activeMenuItem?.segments?.length"
        class="fixed left-0 right-0 top-[100%] bg-black/25 z-10 h-screen"
        @mouseover="activeMenuItem = undefined"
      />
    </transition>
  </template>
</template>

<style scoped lang="scss">
.slide-right-enter-active {
  transition: all 0.4s ease;
}
.slide-right-leave-active {
}
.slide-right-enter-from {
  transform: translateX(20px);
  opacity: 0;
}
.slide-right-leave-to {
  height: 0;
}
</style>
